export default {
  title: 'Perguntas frequentes',
  questions: [
    {
      title: '1. Como funciona a Plataforma?',
      body: `
        A plataforma reúne imóveis disponíveis em diferentes tipos de venda, todos acessíveis em um só lugar.

        **Venda Direta**: imóveis que podem ser comprados digitalmente pela plataforma, com segurança e apoio de corretores de imóveis parceiros.

        **Leilões**: imóveis vendidos por leiloeiros. Os leilões acontecem nos sites dos próprios leiloeiros, mas você encontra e acessa esses imóveis a partir da plataforma.

        __{color:#666666}👉 **Dica**: use os filtros de Tipo de Venda para escolher o caminho que mais se encaixa no que você procura.{/color}__
      `,
    },
    {
      title: '2. Quais são as modalidades de venda disponíveis na plataforma?',
      body: `
        Cada modalidade define como você pode enviar propostas ou participar de disputas pelos imóveis.

        **Leilão (disputa aberta)**: todos os interessados podem dar lances, que ficam visíveis em tempo real até o encerramento.

        **Melhor Proposta (disputa fechada)**: cada comprador envia sua melhor oferta dentro do prazo. As propostas ficam em sigilo e só são reveladas após o término.

        **Fila Única (por ordem de envio)**: a primeira proposta válida é enviada para análise do vendedor.

        __{color:#666666}👉 **Dica**: se você busca previsibilidade, a **Fila Única** pode ser a opção ideal. Já quem busca oportunidades, pode se beneficiar do **Leilão** ou da **Melhor Proposta**.{/color}__
      `,
    },
    {
      title: '3. Como participar dos diferentes tipos de venda na plataforma?',
      body: `
        Cada tipo de venda tem uma forma própria de participação. Ao escolher o imóvel na plataforma, basta seguir as instruções indicadas no botão de ação:

        **Leilão**: clique em **“Ver Leilão”**. Você será direcionado ao site do leiloeiro responsável, onde fará o cadastro, dará os lances e acompanhará o processo em tempo real.

        __{color:#666666}👉 **Dica**: leia sempre o edital do leilão, pois ele contém todas as regras de participação.{/color}__

        **Melhor Proposta**: clique em **“Participe da Melhor Proposta”**. Envie sua oferta dentro do prazo definido. As propostas ficam em sigilo até a abertura, quando o vendedor analisa todas de forma conjunta.

        __{color:#666666}👉 **Dica**: envie sua melhor oferta logo de início, pois não há lances sucessivos.{/color}__

        __{color:#666666}ℹ️ **Atenção**: se você estiver sendo assessorado por um canal de vendas (imobiliária ou corretor), lembre-se de incluir o código desse canal ao enviar sua proposta.{/color}__

        **Fila Única**: clique em **“Comprar”**. A primeira proposta válida enviada pela plataforma é encaminhada para análise do vendedor. Se aprovada, a negociação segue para contrato, escritura e registro.

        __{color:#666666}👉 **Dica**: prepare sua documentação com antecedência para ganhar agilidade, já que vale a ordem de envio.{/color}__

        __{color:#666666}ℹ️ **Atenção**: se você estiver sendo assessorado por um canal de vendas (imobiliária ou corretor), lembre-se de incluir o código desse canal ao enviar sua proposta.{/color}__

      `,
    },
    {
      title: '4. O que significam as categorias de venda na plataforma?',
      body: `
        As categorias são apenas uma forma de identificar imóveis em condições específicas de venda.

        **1º Leilão**: corresponde ao primeiro leilão (1ª praça) previsto na Lei 9.514/97, que rege a retomada extrajudicial dos imóveis financiados por alienação fiduciária.

        **2º Leilão**: segunda oportunidade de venda (2ª praça), geralmente com condições mais atrativas.

        **Pré-Venda**: oportunidade de comprar imóveis que só seriam disponibilizados daqui a alguns meses. Eles possuem pendências simples, mas que não impedem a venda. Ao comprar, você garante o imóvel com preço congelado, enquanto o vendedor resolve a pendência. A escritura só acontece após essa regularização.

        **Outras**: todos os imóveis que **não se enquadram nas categorias anteriores**.

        __{color:#666666}👉 **Dica**: acompanhar imóveis em **2º Leilão** ou **Pré-Venda** pode trazer boas oportunidades de desconto.{/color}__
      `,
    },
    {
      title: '5. O que significam os valores exibidos na plataforma?',
      body: `
        Cada tipo de venda tem regras próprias, por isso os valores podem variar. Na plataforma, você vai encontrar:

        **Valor de Avaliação**: preço estimado por empresa especializada contratada pelo **vendedor**.

        **Valor de Venda**: valor mínimo pelo qual o imóvel pode ser vendido nos casos de **Fila Única**.

        **Lance Mínimo**: valor inicial exigido para participação em **Leilões**.

        **Proposta Mínima**: valor de entrada aceito para enviar ofertas em **Melhor Proposta**.
      `,
    },
    {
      title: '6. Como é calculado o deságio (desconto)?',
      body: `
        O deságio mostra o desconto real do imóvel, comparando o valor de venda com o valor de avaliação.

        Se o deságio não estiver informado, significa que ainda não há um valor de avaliação realizado pelo vendedor para aquele imóvel.
      `,
    },
    {
      title:
        '7. As informações do imóvel (fotos, descrição, documentos e ocupação) refletem a situação atual?',
      body: `
        Nem sempre. Os dados exibidos na plataforma — como imagens, localização, documentos, descrição e situação de ocupação — são meramente ilustrativos e baseados nas informações fornecidas pelos vendedores. Eles podem não refletir a condição atual do imóvel.

        A Resale atua como plataforma de listagem e não se responsabiliza pela veracidade ou atualização dessas informações.

        __{color:#666666}⚠️ **Importante**: as decisões de compra devem ser tomadas com base nas diligências realizadas pelos Proponentes Compradores, conforme o processo de venda da plataforma. Sempre que possível, recomendamos visitar o imóvel ou contar com apoio de especialistas.{/color}__
      `,
    },
    {
      title: '8. O que acontece se o imóvel estiver ocupado?',
      body: `
        **Quem é responsável pela desocupação?**

        A desocupação é de responsabilidade do comprador, salvo quando a Ficha do Imóvel indicar o contrário.

        **Como funciona o processo de desocupação?**

        Tecnicamente, o nome correto desse processo é reintegração de posse — embora alguns advogados utilizem o termo imissão de posse, por entenderem que os compradores estão obtendo a posse pela primeira vez.

        <u>Na prática:</u>
        <span style="padding-left: 16px;">1. O comprador tem legitimidade para ingressar com ação judicial e pleitear a reintegração de posse;</span>
        <span style="padding-left: 16px;">2. Esse processo pode ser amigável, mas, quando não há acordo, segue por decisão judicial;</span>
        <span style="padding-left: 16px;">3. Por se tratar de uma medida judicial, é fundamental contar com assessoria jurídica especializada antes e após a compra.</span>

        **Os ocupantes podem se recusar a sair?**

        Sim, é comum que os ocupantes resistam ou tentem se recusar a deixar o imóvel. No entanto, a Lei é clara: desde 1997, com a Lei 9.514/97 (alienação fiduciária), o Judiciário garante a reintegração em favor do comprador.

        __{color:#666666}⚠️ **Importante**: em nenhuma hipótese o comprador deve abordar diretamente os ocupantes. Sempre busque a orientação de um advogado para conduzir o processo de forma segura e sem constrangimentos para nenhuma das partes.{/color}__
      `,
    },
    {
      title: '9. É possível visitar os imóveis antes de comprar?',
      body: `
        Alguns imóveis permitem visitas presenciais. Quando isso for possível, você verá o botão “Agendar Visita” disponível na Ficha do Imóvel.

        Nos demais casos — especialmente em imóveis ocupados — não é possível realizar visitas internas. Nesses casos, recomendamos analisar com atenção a documentação disponível na Ficha do Imóvel e, sempre que necessário, contar com apoio de especialistas para avaliar a oportunidade.

        __{color:#666666}👉 **Dica**: utilize o filtro “Aceita Visita” na plataforma para visualizar apenas os imóveis que permitem agendamento.{/color}__
      `,
    },
    {
      title: '10. Como funciona a análise jurídica do imóvel?',
      body: `
        O comprador é o responsável por realizar a análise jurídica, física e documental do imóvel, assumindo os custos e riscos dessa verificação.

        O imóvel é vendido no estado em que se encontra, na forma “ad corpus”, ou seja, sem possibilidade de revisão do preço ou rescisão por diferenças de área.

        Em relação a dívidas e despesas propter rem (como IPTU e condomínio), siga sempre o que está descrito na Ficha do Imóvel, que traz as condições aplicáveis a este vendedor.

        __{color:#666666}👉 **Dica**: fazer essa análise completa antes de enviar a proposta garante mais segurança e ajuda a evitar riscos futuros.{/color}__
      `,
    },
    {
      title: '11. Como funciona o pós-venda do imóvel?',
      body: `
        O pós-venda é a etapa que garante que você receba o imóvel com segurança e de acordo com a lei.

        Nesses casos, todo o processo de pós-venda é realizado pela Resale, por meio da Pagimovel, incluindo o cumprimento das exigências legais, o registro da venda e o apoio nos trâmites burocráticos. Esse serviço é formalizado por contrato específico que define os serviços prestados, obrigações e valores acordados.
      `,
    },
  ],
}
